/**
 * Rich content components for `/pop/ai-mba`:
 *   · FAQAccordion         — expandable Q&A
 *   · CurriculumExplorer   — click a module to expand outcomes + hours + capstone brief
 *   · FormatFitQuiz        — 3-Q quiz that recommends Fast Track vs Full-Time
 *   · CapstoneGallery      — 6 sample capstone teaser cards
 *   · MultiStepInterest    — 3-step interest form with progress bar
 *
 * All components use only shadcn + lucide-react + tailwind. No new deps.
 */
import React, { useState } from 'react';
import {
  ChevronDown, HelpCircle, BookOpen, Clock, Target, Rocket, Timer, Calendar,
  ArrowRight, ArrowLeft, CheckCircle2, Loader2, Send, Building2, GraduationCap,
  Beaker, Award, Zap, TrendingUp, Users, Layers, Sparkles, Compass, Brain,
  Lightbulb, ShieldCheck, Factory, LineChart, BarChart3,
} from 'lucide-react';
import { popMBASubmitInterest } from '../../lib/popApi';


// ============================================================================
// FAQAccordion
// ============================================================================
const FAQItem = ({ q, a, open, onToggle, i }) => (
  <div className="border-b border-white/10 last:border-b-0" data-testid={`faq-item-${i}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-start gap-3 py-4 px-1 text-left hover:bg-white/[0.02] transition-colors"
      aria-expanded={open}
      data-testid={`faq-toggle-${i}`}
    >
      <HelpCircle className={`w-4 h-4 mt-1 flex-shrink-0 transition-colors ${open ? 'text-amber-300' : 'text-white/40'}`} />
      <span className={`flex-1 text-sm font-serif font-semibold transition-colors ${open ? 'text-white' : 'text-white/85'}`}>{q}</span>
      <ChevronDown className={`w-4 h-4 mt-1 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180 text-amber-300' : 'text-white/40'}`} />
    </button>
    <div
      className="overflow-hidden transition-[max-height,opacity] duration-500 ease-in-out"
      style={{ maxHeight: open ? '480px' : '0px', opacity: open ? 1 : 0 }}
    >
      <div className="px-8 pb-5 pt-1 text-[13px] text-white/70 leading-relaxed" data-testid={`faq-answer-${i}`}>
        {a}
      </div>
    </div>
  </div>
);

export const FAQAccordion = ({ items = [], initialOpen = 0 }) => {
  const [openIdx, setOpenIdx] = useState(initialOpen);
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/40 divide-y divide-white/5">
      {items.map((it, i) => (
        <FAQItem
          key={i}
          i={i}
          q={it.q}
          a={it.a}
          open={openIdx === i}
          onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
        />
      ))}
    </div>
  );
};


// ============================================================================
// CurriculumExplorer — 12 module cards with click-to-expand detail
// ============================================================================
export const MBA_MODULES = [
  { icon: Brain,        title: 'AI First Principles',           track: 'FOUNDATION', hours: 24, outcomes: [
      'Distinguish generative AI from classical ML at the board level',
      'Read a model card and evaluate real-world limitations',
      'Frame AI investments through cost, accuracy and risk lenses',
    ], capstone: 'Write a 2-page AI feasibility memo for a real workflow at your firm.',
    certAlignment: [{ body: 'AWS · Google · Microsoft', cert: 'AI/ML foundation exams' }] },
  { icon: BarChart3,    title: 'Business Analysis & Data-Driven Decisions',    track: 'FOUNDATION', hours: 28, outcomes: [
      'Diagnose reporting vs analytics vs prediction problems',
      'Elicit, structure and prioritise business requirements (BABOK-aligned)',
      'Build a decision dashboard that ties KPIs to outcomes',
      'Communicate uncertainty and trade-offs to non-technical stakeholders',
    ], capstone: 'Prototype a live dashboard for one function (sales / ops / HR) with a full BA requirements pack.',
    certAlignment: [
      { body: 'IIBA (International Institute of Business Analysis)', cert: 'ECBA · CCBA · CBAP · CBDA' },
    ] },
  { icon: Compass,      title: 'Strategy, Consulting & Digital Business',      track: 'FOUNDATION', hours: 30, outcomes: [
      'Structure a client problem with MECE, issue trees and pyramid principle',
      'Map a value chain and identify AI-augment opportunities',
      'Prioritise using effort × impact × strategic fit',
      'Craft a 12–24 month AI transformation roadmap with a client-ready storyline',
    ], capstone: 'Present a boardroom-grade 24-month roadmap and consulting deliverable for your organisation.',
    certAlignment: [
      { body: 'ICMCI / IMCI (Institute of Management Consultants of India)', cert: 'CMC (Certified Management Consultant)' },
      { body: 'MCA (Management Consultancies Association)', cert: 'Consulting practitioner alignment' },
    ] },
  { icon: TrendingUp,   title: 'AI in Marketing & Sales',        track: 'FUNCTIONAL', hours: 26, outcomes: [
      'Design segmentation & personalisation at scale',
      'Build attribution models that inform spend',
      'Evaluate generative content risks vs upside',
    ], capstone: 'Launch a 4-week AI-driven marketing pilot with measurable lift.',
    certAlignment: [{ body: 'AMA · DMI', cert: 'PCM · DMI Pro (elective bridge)' }] },
  { icon: Factory,      title: 'AI in Operations & Supply Chain', track: 'FUNCTIONAL', hours: 28, outcomes: [
      'Apply demand forecasting to reduce working capital',
      'Design predictive maintenance for critical assets',
      'Integrate AI into procurement, warehousing and logistics',
    ], capstone: 'Redesign one end-to-end supply-chain flow with AI checkpoints.',
    certAlignment: [
      { body: 'ASCM (APICS)', cert: 'CSCP · CPIM · CLTD' },
      { body: 'ISM', cert: 'CPSM (elective bridge)' },
    ] },
  { icon: Users,        title: 'AI in Human Capital',            track: 'FUNCTIONAL', hours: 22, outcomes: [
      'Deploy AI for skills mapping, hiring and internal mobility',
      'Build ethical guardrails around workforce analytics',
      'Design learning journeys personalised via AI',
    ], capstone: 'Publish a 12-week AI-augmented L&D program for your firm.',
    certAlignment: [
      { body: 'SHRM', cert: 'SHRM-CP · SHRM-SCP (bridge)' },
      { body: 'CIPD', cert: 'CIPD Level 5 (bridge)' },
    ] },
  { icon: LineChart,    title: 'AI in Finance & Risk',           track: 'FUNCTIONAL', hours: 26, outcomes: [
      'Deploy AI for forecasting, close automation and fraud',
      'Understand model governance for regulated finance',
      'Evaluate AI-driven credit and market risk models',
    ], capstone: 'Automate one month-end close activity with a working prototype.',
    certAlignment: [
      { body: 'CFA Institute', cert: 'CFA Level I (bridge)' },
      { body: 'GARP', cert: 'FRM Part I (bridge)' },
    ] },
  { icon: ShieldCheck,  title: 'AI Ethics, Governance & Risk',   track: 'ENTERPRISE', hours: 22, outcomes: [
      'Apply EU AI Act & Indian DPDP Act to real cases',
      'Design a model-review board & audit trail',
      'Balance innovation velocity with responsible deployment',
    ], capstone: 'Draft an internal AI governance charter for your organisation.',
    certAlignment: [
      { body: 'IAPP', cert: 'AIGP (AI Governance Professional) · CIPP/E · CDPSE' },
      { body: 'ISACA', cert: 'CGEIT · CRISC' },
    ] },
  { icon: Layers,       title: 'AI Product, Project & Program Management', track: 'ENTERPRISE', hours: 28, outcomes: [
      'Manage AI projects with data & model dependencies (PMBOK 7 + Agile hybrid)',
      'Apply Disciplined Agile & Scrum to cross-functional AI squads',
      'Set OKRs that reward outcomes over model accuracy',
      'Build cross-functional squads that ship AI to production',
    ], capstone: 'Ship one AI feature end-to-end from charter → sprint plan → deployment, PMP-style.',
    certAlignment: [
      { body: 'PMI (Project Management Institute)', cert: 'PMP · PMI-ACP · Disciplined Agile Scrum Master · PfMP' },
      { body: 'Scrum.org / Scrum Alliance', cert: 'PSM I · CSM (bridge)' },
    ] },
  { icon: Building2,    title: 'Enterprise Change & Adoption',   track: 'ENTERPRISE', hours: 24, outcomes: [
      'Diagnose adoption barriers and design workarounds',
      'Apply ADKAR + Kotter models to real AI rollouts',
      'Communicate AI at every level from board to shopfloor',
      'Sustain change with reinforcement rhythms & rewards',
    ], capstone: 'Design a 90-day ADKAR-aligned adoption sprint for one AI initiative.',
    certAlignment: [
      { body: 'Prosci', cert: 'Prosci ADKAR / Change Management Practitioner' },
      { body: 'APMG', cert: 'Change Management Foundation & Practitioner' },
    ] },
  { icon: Lightbulb,    title: 'Entrepreneurship & Innovation',  track: 'CAPSTONE',   hours: 24, outcomes: [
      'Identify AI-native business models in your industry',
      'Build a first prototype with modern LLM toolchains',
      'Pitch a go/no-go recommendation to a mock investor panel',
    ], capstone: 'Take one venture idea from insight → prototype → 3-min pitch.',
    certAlignment: [{ body: 'Lean Startup · YC playbook', cert: 'Portfolio-based (no external exam)' }] },
  { icon: Rocket,       title: 'Live Capstone Studio',           track: 'CAPSTONE',   hours: 40, outcomes: [
      'Solve one real, sponsor-briefed business problem',
      'Ship a working prototype + business case + board memo',
      'Defend your work in a live industry panel review',
    ], capstone: 'Present your capstone to a live panel of industry judges.',
    certAlignment: [{ body: 'SAMANVAI', cert: 'AI Business Transformation Professional (Level 4)' }] },
];

const TRACK_STYLES = {
  FOUNDATION: 'text-emerald-300 border-emerald-300/30 bg-emerald-300/[0.06]',
  FUNCTIONAL: 'text-sky-300 border-sky-300/30 bg-sky-300/[0.06]',
  ENTERPRISE: 'text-violet-300 border-violet-300/30 bg-violet-300/[0.06]',
  CAPSTONE:   'text-amber-300 border-amber-300/30 bg-amber-300/[0.06]',
};

export const CurriculumExplorer = () => {
  const [openIdx, setOpenIdx] = useState(0);
  const [track, setTrack] = useState('ALL');

  const modules = track === 'ALL' ? MBA_MODULES : MBA_MODULES.filter((m) => m.track === track);

  return (
    <>
      <div className="flex flex-wrap gap-1.5 mb-5" data-testid="curriculum-track-filters">
        {['ALL', 'FOUNDATION', 'FUNCTIONAL', 'ENTERPRISE', 'CAPSTONE'].map((t) => (
          <button
            key={t}
            onClick={() => setTrack(t)}
            data-testid={`curriculum-track-${t.toLowerCase()}`}
            className={`px-3 py-1.5 rounded-full text-[10px] tracking-widest font-bold transition ${
              track === t ? 'bg-amber-300 text-slate-900' : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="curriculum-module-grid">
        {modules.map((m) => {
          const globalIdx = MBA_MODULES.indexOf(m);
          const open = openIdx === globalIdx;
          return (
            <div
              key={m.title}
              data-testid={`curriculum-module-${globalIdx}`}
              className={`rounded-xl border transition-all ${
                open ? 'border-amber-300/60 bg-slate-900/70 shadow-lg shadow-amber-300/[0.06]' : 'border-white/10 bg-slate-900/40 hover:border-white/25'
              } ${open ? 'md:col-span-2 lg:col-span-3' : ''}`}
            >
              <button
                onClick={() => setOpenIdx(open ? -1 : globalIdx)}
                data-testid={`curriculum-module-toggle-${globalIdx}`}
                className="w-full text-left p-4 flex items-start gap-3"
              >
                <div className={`w-9 h-9 rounded-lg border flex items-center justify-center flex-shrink-0 ${TRACK_STYLES[m.track] || TRACK_STYLES.FOUNDATION}`}>
                  <m.icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-[9px] tracking-widest font-bold uppercase ${TRACK_STYLES[m.track]?.split(' ')[0] || 'text-white/50'}`}>
                    {m.track} · {m.hours}h
                  </div>
                  <div className="text-sm font-serif font-semibold text-white leading-tight mt-0.5">
                    {m.title}
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180 text-amber-300' : 'text-white/40'}`} />
              </button>

              {open && (
                <div className="px-4 pb-5 pt-1 border-t border-white/10 grid md:grid-cols-2 gap-5" data-testid={`curriculum-module-detail-${globalIdx}`}>
                  <div>
                    <div className="text-[10px] tracking-widest font-bold text-amber-300 mb-2">LEARNING OUTCOMES</div>
                    <ul className="space-y-1.5">
                      {m.outcomes.map((o, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-white/75 leading-snug">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 mt-0.5 flex-shrink-0" />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>

                    {m.certAlignment && m.certAlignment.length > 0 && (
                      <div className="mt-4" data-testid={`curriculum-module-certs-${globalIdx}`}>
                        <div className="flex items-center gap-1.5 text-[10px] tracking-widest font-bold text-sky-300 mb-2">
                          <Award className="w-3 h-3" /> GLOBAL CERTIFICATION ALIGNMENT
                        </div>
                        <ul className="space-y-1.5">
                          {m.certAlignment.map((c, i) => (
                            <li key={i} className="text-[12px] leading-snug">
                              <span className="text-white/85 font-semibold">{c.body}</span>
                              <span className="text-white/40"> · </span>
                              <span className="text-sky-200">{c.cert}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="rounded-lg bg-amber-300/[0.04] border border-amber-300/20 p-4">
                    <div className="flex items-center gap-2 text-[10px] tracking-widest font-bold text-amber-300 mb-2">
                      <Beaker className="w-3 h-3" /> CAPSTONE BRIEF
                    </div>
                    <p className="text-[13px] text-white/80 leading-relaxed italic">{m.capstone}</p>
                    <div className="mt-3 flex items-center gap-3 text-[10px] text-white/50">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {m.hours} hours</span>
                      <span className="inline-flex items-center gap-1"><Target className="w-3 h-3" /> {m.track.toLowerCase()} track</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};


// ============================================================================
// FormatFitQuiz — 3-question decision helper
// ============================================================================
const QUIZ_QUESTIONS = [
  {
    id: 'time',
    prompt: 'How much focused study time can you commit per week?',
    options: [
      { key: '3-5h',   label: '3–5 hours',    weight: { fast_track: 0, full_time: 3 } },
      { key: '6-10h',  label: '6–10 hours',   weight: { fast_track: 2, full_time: 2 } },
      { key: '10-15h', label: '10–15 hours',  weight: { fast_track: 3, full_time: 1 } },
      { key: '15+h',   label: '15+ hours',    weight: { fast_track: 4, full_time: 0 } },
    ],
  },
  {
    id: 'urgency',
    prompt: 'How urgently do you need the capability at work?',
    options: [
      { key: 'this-quarter',  label: 'This quarter — an AI initiative is on my desk', weight: { fast_track: 5, full_time: 0 } },
      { key: '6-months',      label: 'Within 6 months — I want depth quickly',       weight: { fast_track: 3, full_time: 1 } },
      { key: 'this-year',     label: 'This year — building for the next role',        weight: { fast_track: 1, full_time: 3 } },
      { key: 'career-pivot',  label: 'Career-defining pivot — I want full immersion', weight: { fast_track: 0, full_time: 5 } },
    ],
  },
  {
    id: 'goal',
    prompt: 'What outcome matters most?',
    options: [
      { key: 'promotion',   label: 'Get promoted / lead an AI initiative',      weight: { fast_track: 4, full_time: 1 } },
      { key: 'switch',      label: 'Switch to an AI-adjacent role',            weight: { fast_track: 2, full_time: 3 } },
      { key: 'entrepren',   label: 'Start something new (venture / advisory)', weight: { fast_track: 2, full_time: 3 } },
      { key: 'degree',      label: 'Earn a full-scale MBA credential',        weight: { fast_track: 0, full_time: 5 } },
    ],
  },
];

export const FormatFitQuiz = ({ onCTAClick }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const pick = (qid, opt) => {
    const next = { ...answers, [qid]: opt };
    setAnswers(next);
    if (step < QUIZ_QUESTIONS.length - 1) setStep(step + 1);
    else setShowResult(true);
  };

  const reset = () => { setAnswers({}); setStep(0); setShowResult(false); };

  const score = { fast_track: 0, full_time: 0 };
  Object.values(answers).forEach((a) => {
    score.fast_track += a.weight.fast_track;
    score.full_time  += a.weight.full_time;
  });
  const winner = score.fast_track >= score.full_time ? 'fast_track' : 'full_time';
  const confidence = Math.round((Math.max(score.fast_track, score.full_time) / (score.fast_track + score.full_time || 1)) * 100);

  if (showResult) {
    return (
      <div className="rounded-2xl border border-amber-300/30 bg-slate-900/60 p-6" data-testid="format-quiz-result">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-amber-300" />
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300">RECOMMENDED FOR YOU</div>
        </div>
        <div className="font-serif text-3xl sm:text-4xl font-bold text-white mb-2" data-testid="format-quiz-winner">
          {winner === 'fast_track' ? '12-week Fast Track' : '24-month Full-Time'}
        </div>
        <div className="text-sm text-white/70 leading-relaxed mb-4 max-w-lg">
          {winner === 'fast_track'
            ? 'Your combination of committed hours, business urgency and outcome goals points to the intensive 12-week pathway — capability in weeks, capstone in months.'
            : 'Your career-pivot orientation and appetite for depth line up best with the 24-month full-time architecture — four semesters of foundation, functional depth, enterprise leadership and capstone.'}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-white/50 mb-5">
          <div>Confidence <span className="text-white/90 tabular-nums font-semibold">{confidence}%</span></div>
          <div className="h-1 flex-1 max-w-[160px] rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-amber-300 transition-all duration-1000" style={{ width: `${confidence}%` }} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={onCTAClick} data-testid="format-quiz-cta"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-300 hover:bg-amber-200 text-slate-900 text-xs font-semibold shadow">
            <Send className="w-3.5 h-3.5" /> Express interest for this pathway
          </button>
          <button onClick={reset} data-testid="format-quiz-reset"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 text-xs">
            <ArrowLeft className="w-3.5 h-3.5" /> Retake
          </button>
        </div>
      </div>
    );
  }

  const q = QUIZ_QUESTIONS[step];
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6" data-testid="format-quiz">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-4">
        {QUIZ_QUESTIONS.map((_, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? 'bg-amber-300' : 'bg-white/10'}`} />
        ))}
        <div className="text-[10px] font-bold text-white/50 ml-2 tabular-nums">{step + 1}/{QUIZ_QUESTIONS.length}</div>
      </div>
      <div className="text-[10px] tracking-widest font-bold text-amber-300 mb-1">QUICK 3-QUESTION QUIZ</div>
      <div className="font-serif text-xl text-white mb-4" data-testid={`format-quiz-prompt-${step}`}>{q.prompt}</div>
      <div className="grid sm:grid-cols-2 gap-2">
        {q.options.map((opt) => (
          <button
            key={opt.key}
            onClick={() => pick(q.id, opt)}
            data-testid={`format-quiz-opt-${step}-${opt.key}`}
            className="text-left px-4 py-3 rounded-lg bg-white/5 hover:bg-amber-300/10 border border-white/10 hover:border-amber-300/50 transition group flex items-center justify-between gap-2"
          >
            <span className="text-sm text-white/85 group-hover:text-white">{opt.label}</span>
            <ArrowRight className="w-3.5 h-3.5 text-white/30 group-hover:text-amber-300 group-hover:translate-x-0.5 transition-transform" />
          </button>
        ))}
      </div>
      {step > 0 && (
        <button onClick={() => setStep(step - 1)}
          className="mt-4 text-[11px] text-white/50 hover:text-white/85 inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
      )}
    </div>
  );
};


// ============================================================================
// CapstoneGallery — 6 sample capstone teaser cards
// ============================================================================
export const CAPSTONE_SAMPLES = [
  { icon: Factory,     sector: 'Manufacturing', title: 'Predictive maintenance for a cement kiln',
      brief: 'Ship a sensor-fusion model that predicts unplanned downtime 72 hours in advance for a 4,000 TPD line.',
      deliverables: ['Prototype notebook + model card', 'Rollout plan for 3 sites', 'Board memo with ROI'] },
  { icon: LineChart,   sector: 'BFSI',          title: 'Retail credit underwriting with alt data',
      brief: 'Blend bureau + open banking + telecom signals into a risk score with model-explainability guardrails.',
      deliverables: ['Model + fairness audit', 'Ops runbook', 'Regulator-ready governance charter'] },
  { icon: TrendingUp,  sector: 'FMCG',          title: 'AI-personalised trade promotion engine',
      brief: 'Design an outlet-level promo recommender that lifts sell-out without eroding margin.',
      deliverables: ['Recommender + AB harness', 'Pilot playbook', '4-week revenue impact readout'] },
  { icon: Users,       sector: 'HR / L&D',      title: 'Skills graph & internal mobility for a 25k-person firm',
      brief: 'Map skills-to-roles-to-projects and surface internal opportunities via a matching engine.',
      deliverables: ['Skills graph schema', 'Matching engine prototype', 'Adoption plan for HRBPs'] },
  { icon: ShieldCheck, sector: 'Public sector', title: 'GenAI grievance triage for a citizen portal',
      brief: 'Auto-triage 40k weekly complaints into the right department with human-in-the-loop safeguards.',
      deliverables: ['Triage model + guardrails', 'Escalation SOP', '30-day success metrics'] },
  { icon: BarChart3,   sector: 'Retail',        title: 'Store-of-the-future analytics stack',
      brief: 'Fuse footfall, checkout and CCTV to power staffing, planogram and shrinkage decisions.',
      deliverables: ['Data model', 'Store-manager dashboard', '3-format pilot plan'] },
];

export const CapstoneGallery = () => (
  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
    {CAPSTONE_SAMPLES.map((c, i) => (
      <div key={i} data-testid={`capstone-sample-${i}`} className="rounded-xl border border-white/10 bg-slate-900/40 p-5 hover:border-amber-300/40 transition group">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-amber-300/10 border border-amber-300/30 flex items-center justify-center flex-shrink-0">
            <c.icon className="w-3.5 h-3.5 text-amber-300" />
          </div>
          <div className="text-[9px] tracking-widest font-bold text-amber-300 uppercase">{c.sector}</div>
        </div>
        <div className="font-serif text-sm font-semibold text-white leading-tight mb-2">{c.title}</div>
        <p className="text-[12px] text-white/60 leading-relaxed mb-3">{c.brief}</p>
        <div className="text-[10px] tracking-widest font-bold text-white/40 uppercase mb-1.5">Deliverables</div>
        <ul className="space-y-1">
          {c.deliverables.map((d, j) => (
            <li key={j} className="flex items-start gap-1.5 text-[11px] text-white/70">
              <CheckCircle2 className="w-3 h-3 text-emerald-300 mt-0.5 flex-shrink-0" />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);


// ============================================================================
// MultiStepInterest — 3-step form with progress bar (replaces original single-page form)
// ============================================================================
const ROLE_OPTIONS = [
  { key: 'university_director', label: 'University Director / Dean',      Icon: GraduationCap },
  { key: 'faculty',             label: 'Faculty / Academic',              Icon: BookOpen },
  { key: 'industry_leader',     label: 'Industry Expert / Executive',     Icon: Building2 },
  { key: 'mba_student',         label: 'Working Professional / Student',  Icon: Users },
  { key: 'corporate_ld_head',   label: 'Corporate L&D / CHRO',            Icon: Award },
  { key: 'other',               label: 'Other',                            Icon: Compass },
];

const FORMAT_OPTIONS = [
  { key: 'fast_track', label: '12-week Fast Track',   sub: 'Weekly cohorts · intense',  Icon: Zap },
  { key: 'full_time',  label: '24-month Full-Time',   sub: '4 semesters · immersive',   Icon: Timer },
  { key: 'either',     label: 'Both — help me decide', sub: 'Send me a comparison call', Icon: Compass },
];

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v || '').trim());
const phoneOk = (v) => /^\+?[\d\s\-().]{6,20}$/.test((v || '').trim());

export const MultiStepInterest = ({ presetFormat = '', onSuccess }) => {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({
    learner_type: '',
    name: '',
    email: '',
    phone: '',
    institution_or_org: '',
    format_preference: presetFormat || '',
    goal: '',
    consent_marketing: true,
  });

  const upd = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setErr(''); };
  const pick = (k, v) => { setForm({ ...form, [k]: v }); setErr(''); };

  const stepValid = (() => {
    if (step === 0) return !!form.learner_type;
    if (step === 1) return form.name.trim().length >= 2 && emailOk(form.email) && phoneOk(form.phone);
    if (step === 2) return !!form.format_preference;
    return true;
  })();

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      await popMBASubmitInterest(form);
      setDone(true);
      if (onSuccess) onSuccess();
    } catch (ex) {
      // Extract a user-safe error string. Pydantic 422 returns detail as an array of
      // {loc, msg, type} objects — never render that array directly (React crashes
      // on 'Objects are not valid as a React child').
      const d = ex && ex.response && ex.response.data && ex.response.data.detail;
      let msg = 'Could not submit — please retry';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d.length > 0 && typeof d[0]?.msg === 'string') {
        msg = d.map((e) => e.msg).slice(0, 3).join(' · ');
      } else if (ex && typeof ex.message === 'string') {
        msg = ex.message;
      }
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/[0.06] p-8 text-center" data-testid="mba-interest-success">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/20 border border-emerald-400/50 flex items-center justify-center mb-4">
          <CheckCircle2 className="w-7 h-7 text-emerald-300" />
        </div>
        <div className="font-serif text-2xl font-bold text-white mb-2">You&apos;re on the list.</div>
        <div className="text-sm text-white/70 max-w-sm mx-auto">
          A SAMANVAI program advisor will reach out with cohort dates, curriculum and admission details.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-6" data-testid="mba-multi-step-form">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {[0, 1, 2].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${i <= step ? 'bg-amber-300' : 'bg-white/10'}`} />
        ))}
        <div className="text-[10px] font-bold text-white/50 ml-2 tabular-nums" data-testid="mba-step-indicator">{step + 1}/3</div>
      </div>

      {step === 0 && (
        <>
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-1">STEP 1 · WHO YOU ARE</div>
          <div className="font-serif text-2xl text-white mb-5">Which best describes you?</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {ROLE_OPTIONS.map((r) => (
              <button key={r.key} onClick={() => pick('learner_type', r.key)}
                data-testid={`mba-role-${r.key}`}
                className={`text-left px-4 py-3 rounded-lg border transition flex items-center gap-3 ${
                  form.learner_type === r.key
                    ? 'bg-amber-300/10 border-amber-300/60 text-white'
                    : 'bg-white/[0.02] border-white/10 text-white/70 hover:border-white/25'
                }`}>
                <r.Icon className={`w-4 h-4 ${form.learner_type === r.key ? 'text-amber-300' : 'text-white/50'}`} />
                <span className="text-sm">{r.label}</span>
              </button>
            ))}
          </div>
        </>
      )}

      {step === 1 && (
        <>
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-1">STEP 2 · HOW WE REACH YOU</div>
          <div className="font-serif text-2xl text-white mb-5">Contact & organisation.</div>
          <div className="space-y-3">
            <div>
              <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">Full name *</label>
              <input required type="text" value={form.name} onChange={upd('name')} data-testid="mba-name"
                placeholder="Your name"
                className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none" />
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <div>
                <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">Email *</label>
                <input required type="email" value={form.email} onChange={upd('email')} data-testid="mba-email"
                  placeholder="you@institution.edu"
                  className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none" />
              </div>
              <div>
                <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">Phone *</label>
                <input required type="tel" value={form.phone} onChange={upd('phone')} data-testid="mba-phone"
                  placeholder="+91 …"
                  className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none" />
              </div>
            </div>
            <div>
              <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">Organisation / Institution</label>
              <input type="text" value={form.institution_or_org} onChange={upd('institution_or_org')} data-testid="mba-org"
                placeholder="Company or university (optional)"
                className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none" />
            </div>
          </div>
        </>
      )}

      {step === 2 && (
        <>
          <div className="text-[10px] tracking-[0.3em] font-bold text-amber-300 mb-1">STEP 3 · YOUR PATHWAY</div>
          <div className="font-serif text-2xl text-white mb-5">Which format calls to you?</div>
          <div className="grid sm:grid-cols-3 gap-2">
            {FORMAT_OPTIONS.map((f) => (
              <button key={f.key} onClick={() => pick('format_preference', f.key)}
                data-testid={`mba-format-${f.key}`}
                className={`text-left px-4 py-3 rounded-lg border transition ${
                  form.format_preference === f.key
                    ? 'bg-amber-300/10 border-amber-300/60'
                    : 'bg-white/[0.02] border-white/10 hover:border-white/25'
                }`}>
                <f.Icon className={`w-4 h-4 mb-2 ${form.format_preference === f.key ? 'text-amber-300' : 'text-white/50'}`} />
                <div className="text-sm font-semibold text-white/90">{f.label}</div>
                <div className="text-[10px] text-white/50">{f.sub}</div>
              </button>
            ))}
          </div>
          <div className="mt-4">
            <label className="block text-[9px] uppercase tracking-wider font-semibold text-white/50 mb-1">What outcome matters most? (optional)</label>
            <textarea value={form.goal} onChange={upd('goal')} data-testid="mba-goal" rows={2} maxLength={400}
              placeholder="A specific promotion, initiative, credential or pivot…"
              className="w-full px-3 py-2 rounded-md bg-white/5 border border-white/10 text-sm text-white placeholder:text-white/30 focus:border-amber-300/60 focus:outline-none resize-none" />
          </div>
        </>
      )}

      {err && <div className="text-[11px] text-rose-300 mt-4" data-testid="mba-interest-error">{err}</div>}

      <div className="mt-6 flex items-center gap-2">
        {step > 0 && (
          <button onClick={() => setStep(step - 1)} data-testid="mba-back"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-white/70 text-xs">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        )}
        <div className="flex-1" />
        {step < 2 && (
          <button onClick={() => setStep(step + 1)} disabled={!stepValid} data-testid="mba-next"
            className={`inline-flex items-center gap-1 px-5 py-2 rounded-md text-sm font-semibold shadow ${
              stepValid ? 'bg-amber-300 hover:bg-amber-200 text-slate-900' : 'bg-white/10 text-white/40 cursor-not-allowed'
            }`}>
            Next <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
        {step === 2 && (
          <button onClick={submit} disabled={!stepValid || busy} data-testid="mba-submit"
            className={`inline-flex items-center gap-1 px-5 py-2 rounded-md text-sm font-semibold shadow ${
              stepValid && !busy ? 'bg-amber-300 hover:bg-amber-200 text-slate-900' : 'bg-white/10 text-white/40 cursor-not-allowed'
            }`}>
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <><Send className="w-3.5 h-3.5" /> Submit interest</>}
          </button>
        )}
      </div>
    </div>
  );
};


// ============================================================================
// CertificationAlignment — global professional credential map that the
// AI-Enabled MBA prepares learners for. Rendered as an editorial table + FAQ.
// ============================================================================
export const CERT_TRACKS = [
  {
    body: 'PMI (Project Management Institute)',
    domain: 'Project & Program Management',
    color: 'text-sky-300 border-sky-300/30 bg-sky-300/[0.06]',
    certs: [
      { code: 'PMP',          name: 'Project Management Professional' },
      { code: 'PMI-ACP',      name: 'Agile Certified Practitioner' },
      { code: 'DASM · DASSM', name: 'Disciplined Agile Scrum Master / Sr. Scrum Master' },
      { code: 'PfMP',         name: 'Portfolio Management Professional' },
      { code: 'PMI-PBA',      name: 'Professional in Business Analysis' },
    ],
    coveredIn: 'AI Product, Project & Program Management · Live Capstone Studio',
  },
  {
    body: 'IIBA (International Institute of Business Analysis)',
    domain: 'Business Analysis',
    color: 'text-emerald-300 border-emerald-300/30 bg-emerald-300/[0.06]',
    certs: [
      { code: 'ECBA', name: 'Entry Certificate in Business Analysis' },
      { code: 'CCBA', name: 'Certification of Capability in Business Analysis' },
      { code: 'CBAP', name: 'Certified Business Analysis Professional' },
      { code: 'CBDA', name: 'Certification in Business Data Analytics' },
    ],
    coveredIn: 'Business Analysis & Data-Driven Decisions · AI First Principles',
  },
  {
    body: 'ICMCI · IMCI (Institute of Management Consultants of India)',
    domain: 'Management Consulting',
    color: 'text-amber-300 border-amber-300/30 bg-amber-300/[0.06]',
    certs: [
      { code: 'CMC',  name: 'Certified Management Consultant (ICMCI global standard)' },
      { code: 'AMC',  name: 'Associate Management Consultant (IMCI pathway)' },
    ],
    coveredIn: 'Strategy, Consulting & Digital Business · Live Capstone Studio',
  },
  {
    body: 'IAPP · ISACA',
    domain: 'AI Governance, Privacy & Risk',
    color: 'text-violet-300 border-violet-300/30 bg-violet-300/[0.06]',
    certs: [
      { code: 'AIGP',   name: 'AI Governance Professional (IAPP)' },
      { code: 'CIPP/E', name: 'Certified Information Privacy Professional (IAPP)' },
      { code: 'CDPSE',  name: 'Certified Data Privacy Solutions Engineer (ISACA)' },
      { code: 'CGEIT',  name: 'Certified in the Governance of Enterprise IT (ISACA)' },
    ],
    coveredIn: 'AI Ethics, Governance & Risk',
  },
  {
    body: 'ASCM (APICS) · ISM',
    domain: 'Supply Chain & Operations',
    color: 'text-cyan-300 border-cyan-300/30 bg-cyan-300/[0.06]',
    certs: [
      { code: 'CSCP',  name: 'Certified Supply Chain Professional' },
      { code: 'CPIM',  name: 'Certified in Planning & Inventory Management' },
      { code: 'CLTD',  name: 'Certified in Logistics, Transportation & Distribution' },
      { code: 'CPSM',  name: 'Certified Professional in Supply Management (ISM)' },
    ],
    coveredIn: 'AI in Operations & Supply Chain',
  },
  {
    body: 'CFA Institute · GARP',
    domain: 'Finance & Risk',
    color: 'text-lime-300 border-lime-300/30 bg-lime-300/[0.06]',
    certs: [
      { code: 'CFA Level I', name: 'Chartered Financial Analyst (bridge)' },
      { code: 'FRM Part I',  name: 'Financial Risk Manager (bridge)' },
    ],
    coveredIn: 'AI in Finance & Risk',
  },
  {
    body: 'SHRM · CIPD',
    domain: 'Human Capital',
    color: 'text-rose-300 border-rose-300/30 bg-rose-300/[0.06]',
    certs: [
      { code: 'SHRM-CP',  name: 'SHRM Certified Professional' },
      { code: 'SHRM-SCP', name: 'SHRM Senior Certified Professional' },
      { code: 'CIPD L5',  name: 'CIPD Level 5 Associate Diploma (bridge)' },
    ],
    coveredIn: 'AI in Human Capital',
  },
  {
    body: 'Prosci · APMG',
    domain: 'Change Management',
    color: 'text-fuchsia-300 border-fuchsia-300/30 bg-fuchsia-300/[0.06]',
    certs: [
      { code: 'Prosci CMP', name: 'Prosci Change Management Practitioner (ADKAR)' },
      { code: 'APMG CM',    name: 'APMG Change Management Foundation & Practitioner' },
    ],
    coveredIn: 'Enterprise Change & Adoption',
  },
];

export const CertificationAlignment = () => (
  <div data-testid="certification-alignment">
    {/* Intro rail */}
    <div className="grid md:grid-cols-3 gap-3 mb-6">
      {[
        { icon: Award,      k: '8+', l: 'Global bodies aligned', s: 'PMI · IIBA · ICMCI · IAPP · ISACA · ASCM · CFA · SHRM' },
        { icon: BookOpen,   k: '25+', l: 'Portable credentials', s: 'From ECBA to PMP · CBAP · CMC · CFA · AIGP · CSCP' },
        { icon: Sparkles,   k: '100%', l: 'Woven into curriculum', s: 'Every module maps to a globally-recognised body of knowledge' },
      ].map((r) => (
        <div key={r.l} className="rounded-xl border border-white/10 bg-slate-900/40 p-4 flex items-start gap-3">
          <r.icon className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-serif text-2xl text-white tabular-nums leading-none">{r.k}</div>
            <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1.5">{r.l}</div>
            <div className="text-[11px] text-white/60 mt-1 leading-snug">{r.s}</div>
          </div>
        </div>
      ))}
    </div>

    {/* Certification tracks grid */}
    <div className="grid md:grid-cols-2 gap-3">
      {CERT_TRACKS.map((t, i) => (
        <div
          key={t.body}
          data-testid={`cert-track-${i}`}
          className="rounded-xl border border-white/10 bg-slate-900/40 p-5 hover:border-amber-300/40 transition"
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="min-w-0">
              <div className="text-[10px] tracking-[0.28em] font-bold text-white/45 uppercase mb-1">{t.domain}</div>
              <div className="font-serif text-base text-white font-semibold leading-tight">{t.body}</div>
            </div>
            <div className={`px-2 py-0.5 rounded-full text-[9px] font-bold tracking-widest border ${t.color}`}>
              {t.certs.length} CERTS
            </div>
          </div>
          <ul className="mt-3 space-y-1.5">
            {t.certs.map((c) => (
              <li key={c.code} className="flex items-baseline gap-2 text-[12px] leading-snug">
                <span className="font-mono text-amber-300 font-semibold shrink-0 tabular-nums">{c.code}</span>
                <span className="text-white/70">{c.name}</span>
              </li>
            ))}
          </ul>
          <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-white/55">
            <span className="text-white/40 uppercase tracking-widest text-[9px] font-bold mr-1.5">Covered in</span>
            <span className="italic">{t.coveredIn}</span>
          </div>
        </div>
      ))}
    </div>

    {/* Editorial footer note */}
    <div className="mt-6 rounded-xl border border-amber-300/20 bg-amber-300/[0.04] p-4 flex items-start gap-3">
      <Lightbulb className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
      <div className="text-[12px] text-white/75 leading-relaxed">
        <span className="font-semibold text-white">Note.</span> The SAMANVAI AI-Enabled MBA is a stand-alone credential — it does <em>not</em> issue PMI, IIBA, ICMCI, IAPP, ISACA, ASCM, CFA, GARP, SHRM, CIPD, Prosci or APMG certifications. What it does is systematically embed each body of knowledge into the curriculum so graduates are <span className="text-white">exam-ready for the credential(s) most relevant to their pathway</span>. Bridge study-guides, mock papers and application support are provided as part of the Learner Success Kit.
      </div>
    </div>
  </div>
);
